/* A2P 10DLC activation — Slice B of the Managed Numbers rollout.
 *
 * Renders inside ManagedNumbersCard. Shows the current Brand + Campaign
 * status and lets the user kick off auto-registration via a single modal
 * that collects their business identity (LLC + EIN, or individual).
 *
 * The actual Twilio orchestration happens server-side in
 * services/twilio_a2p_provisioning.py — this just collects the form data
 * and surfaces the result.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import {
  ShieldCheck, AlertTriangle, Loader2, RefreshCw, X, Check, Sparkles,
} from "lucide-react";

const INDUSTRIES = [
  "REAL_ESTATE", "RETAIL", "ONLINE", "PROFESSIONAL", "FINANCIAL",
  "AUTOMOTIVE", "AGRICULTURE", "CONSTRUCTION", "EDUCATION", "ENERGY",
  "ENTERTAINMENT", "GOVERNMENT", "HEALTHCARE", "HOSPITALITY",
  "INSURANCE", "LEGAL", "MANUFACTURING", "MEDIA", "NGO", "NOT_FOR_PROFIT",
  "POSTAL", "TECHNOLOGY", "TRANSPORTATION", "OTHER",
];

const STATUS_TONE = {
  VERIFIED: "bg-emerald-100 text-emerald-800 border-emerald-200",
  APPROVED: "bg-emerald-100 text-emerald-800 border-emerald-200",
  PENDING: "bg-amber-100 text-amber-800 border-amber-200",
  IN_PROGRESS: "bg-amber-100 text-amber-800 border-amber-200",
  IN_REVIEW: "bg-amber-100 text-amber-800 border-amber-200",
  FAILED: "bg-rose-100 text-rose-800 border-rose-200",
  REJECTED: "bg-rose-100 text-rose-800 border-rose-200",
  SUSPENDED: "bg-rose-100 text-rose-800 border-rose-200",
};

function StatusPill({ label, status }) {
  const tone = STATUS_TONE[(status || "").toUpperCase()]
    || "bg-stone-100 text-stone-700 border-stone-200";
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${tone}`}>
      {label}: {status || "—"}
    </span>
  );
}

export default function A2pActivationPanel({ ownedCount }) {
  const [reg, setReg] = useState(null);          // {bundle_sid, brand_sid, campaign_sid, brand_status, campaign_status, ...}
  const [ready, setReady] = useState(false);
  const [reason, setReason] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/twilio-numbers/a2p/status");
      setReg(data?.registration || null);
      setReady(!!data?.ready_to_send);
      setReason(data?.reason || null);
    } catch (e) {
      const code = e?.response?.status;
      // Free tier or no subaccount yet — silently hide.
      if (code === 402 || code === 403) {
        setReg(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/twilio-numbers/a2p/refresh");
      setReg(data?.registration || null);
      toast.success("Status refreshed.");
      // Also re-fetch the ready_to_send flag.
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed.");
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="mt-5 pt-5 border-t border-[var(--line)] text-center text-xs text-[var(--ink-2)]">
        <Loader2 size={12} className="animate-spin inline mr-1" /> Loading A2P status…
      </div>
    );
  }

  // Don't show until they actually own a managed number — no point.
  if (!ownedCount) return null;

  // Already registered — show status panel.
  if (reg) {
    return (
      <div className="mt-5 pt-5 border-t border-[var(--line)]" data-testid="a2p-status-panel">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border ${
            ready
              ? "bg-emerald-50 border-emerald-200"
              : "bg-amber-50 border-amber-200"
          }`}>
            {ready
              ? <ShieldCheck size={18} className="text-emerald-600" />
              : <AlertTriangle size={18} className="text-amber-600" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-sm">
              SMS Deliverability {ready ? "· Active" : "· Pending Carrier Review"}
            </div>
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              <StatusPill label="Brand" status={reg.brand_status} />
              <StatusPill label="Campaign" status={reg.campaign_status} />
              <span className="text-[10px] text-[var(--ink-2)] ml-1">
                {reg.brand_type === "LOW_VOLUME_STANDARD" ? "LLC / Low Volume" : "Sole Proprietor"}
              </span>
            </div>
            {!ready && reason && (
              <div className="mt-2 text-xs text-[var(--ink-2)]">
                {reason}
              </div>
            )}
            {reg.brand_failure_reason && (
              <div className="mt-2 text-xs text-rose-700">
                <b>Brand rejected:</b> {reg.brand_failure_reason}
              </div>
            )}
            {reg.campaign_rejection_reason && (
              <div className="mt-2 text-xs text-rose-700">
                <b>Campaign rejected:</b> {reg.campaign_rejection_reason}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={refresh}
            disabled={refreshing}
            className="shrink-0 text-xs px-2 py-1.5 rounded-lg border border-[var(--line)] inline-flex items-center gap-1 hover:bg-[var(--bg-2)]"
            data-testid="a2p-refresh-btn"
          >
            {refreshing ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
            Check status
          </button>
        </div>
      </div>
    );
  }

  // Not registered yet — show activation CTA + modal trigger.
  return (
    <>
      <div className="mt-5 pt-5 border-t border-[var(--line)]" data-testid="a2p-activation-cta">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3">
          <Sparkles size={18} className="text-amber-700 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm text-amber-900">
              Activate SMS Deliverability
            </div>
            <div className="text-xs text-amber-800 mt-1">
              Without A2P 10DLC registration, carriers filter up to <b>30% of your messages</b>
              {" "}as "unregistered traffic". iSpeedToClose absorbs the registration cost ($4–$20 one-time
              + $1.50/mo) — you just confirm your business details.
            </div>
          </div>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="shrink-0 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold px-3 py-2 rounded-lg whitespace-nowrap"
            data-testid="a2p-activate-btn"
          >
            Activate now
          </button>
        </div>
      </div>
      {modalOpen && (
        <A2pSubmitModal
          onClose={() => setModalOpen(false)}
          onSuccess={async () => {
            setModalOpen(false);
            await load();
            toast.success("A2P registration submitted! Carriers typically review within 1–3 business days.");
          }}
        />
      )}
    </>
  );
}


function A2pSubmitModal({ onClose, onSuccess }) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    business_type: "Limited Liability Corporation",
    business_industry: "REAL_ESTATE",
    business_name: "",
    ein: "",
    website_url: "",
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    address_street: "",
    address_city: "",
    address_state: "",
    address_zip: "",
  });
  const isLLC = form.business_type !== "Sole Proprietorship";
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!isLLC) delete payload.ein;
      await api.post("/twilio-numbers/a2p/submit", payload);
      await onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed — check your business details.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto"
         onClick={onClose}
         data-testid="a2p-submit-modal">
      <div className="bg-white rounded-xl max-w-2xl w-full my-8 max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-[var(--line)] px-6 py-4 flex items-center justify-between rounded-t-xl">
          <div>
            <div className="font-display font-extrabold text-lg leading-tight">
              Activate SMS Deliverability
            </div>
            <div className="text-xs text-[var(--ink-2)] mt-0.5">
              Carriers vet this — match details exactly to IRS / state records.
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-[var(--ink-2)] hover:text-[var(--ink)]">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          {/* Business type */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[var(--ink-2)]">
              Business Type
            </label>
            <select
              value={form.business_type}
              onChange={set("business_type")}
              className="input w-full mt-1"
              data-testid="a2p-business-type"
            >
              <option value="Limited Liability Corporation">LLC / Corporation (I HAVE an EIN)</option>
              <option value="Corporation">Corporation</option>
              <option value="Partnership">Partnership</option>
              <option value="Non-profit Corporation">Non-profit</option>
              <option value="Sole Proprietorship">Sole Proprietor / Individual (NO EIN)</option>
            </select>
            <div className="text-[10px] text-[var(--ink-2)] mt-1">
              {isLLC ? (
                <>Any business taxed under an EIN — including single-member LLCs.</>
              ) : (
                <>For individuals operating with no Tax ID. <b>Limited to 1 phone number per Messaging Service.</b></>
              )}
            </div>
          </div>

          {/* Legal name + EIN */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[var(--ink-2)]">
                Legal Business Name *
              </label>
              <input
                value={form.business_name}
                onChange={set("business_name")}
                placeholder="Perrin Properties LLC"
                className="input w-full mt-1"
                required
                data-testid="a2p-business-name"
              />
            </div>
            {isLLC && (
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[var(--ink-2)]">
                  EIN *
                </label>
                <input
                  value={form.ein}
                  onChange={set("ein")}
                  placeholder="12-3456789"
                  className="input w-full mt-1"
                  required
                  data-testid="a2p-ein"
                />
                <div className="text-[10px] text-[var(--ink-2)] mt-1">
                  9 digits, exactly as filed with the IRS.
                </div>
              </div>
            )}
          </div>

          {/* Industry + Website */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[var(--ink-2)]">
                Industry *
              </label>
              <select
                value={form.business_industry}
                onChange={set("business_industry")}
                className="input w-full mt-1"
                data-testid="a2p-industry"
              >
                {INDUSTRIES.map((i) => (
                  <option key={i} value={i}>{i.replace(/_/g, " ")}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[var(--ink-2)]">
                Business Website *
              </label>
              <input
                value={form.website_url}
                onChange={set("website_url")}
                placeholder="https://yoursite.com"
                className="input w-full mt-1"
                required
                data-testid="a2p-website"
              />
              <div className="text-[10px] text-[var(--ink-2)] mt-1">
                Must display SMS opt-in language: "Msg & Data rates may apply. Reply STOP to opt out."
              </div>
            </div>
          </div>

          {/* Authorized rep */}
          <div className="text-xs font-bold uppercase tracking-wider text-[var(--ink-2)] pt-2">
            Authorized Representative
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input value={form.first_name} onChange={set("first_name")} placeholder="First name *" className="input" required data-testid="a2p-first-name" />
            <input value={form.last_name} onChange={set("last_name")} placeholder="Last name *" className="input" required data-testid="a2p-last-name" />
            <input value={form.email} onChange={set("email")} placeholder="Email *" type="email" className="input" required data-testid="a2p-email" />
            <input value={form.phone_number} onChange={set("phone_number")} placeholder="Phone +15551234567 *" className="input" required data-testid="a2p-phone" />
          </div>

          {/* Address */}
          <div className="text-xs font-bold uppercase tracking-wider text-[var(--ink-2)] pt-2">
            Business Address
          </div>
          <AddressAutocomplete
            name="a2p_address_street"
            placeholder="Street address *"
            inputClassName="input w-full"
            dataTestid="a2p-street"
            value={form.address_street}
            onChange={(v) => setForm((f) => ({ ...f, address_street: v }))}
            onParsed={({ street, city, state, zip }) => setForm((prev) => ({
              ...prev,
              address_street: street || prev.address_street,
              address_city: city || prev.address_city,
              address_state: state || prev.address_state,
              address_zip: zip || prev.address_zip,
            }))}
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input value={form.address_city} onChange={set("address_city")} placeholder="City *" className="input" required data-testid="a2p-city" />
            <input value={form.address_state} onChange={set("address_state")} placeholder="State (TX) *" maxLength={2} className="input" required data-testid="a2p-state" />
            <input value={form.address_zip} onChange={set("address_zip")} placeholder="ZIP *" className="input" required data-testid="a2p-zip" />
          </div>

          <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 text-xs text-[var(--ink-2)] mt-2">
            <div className="font-semibold text-[var(--ink)] mb-1">Cost:</div>
            {isLLC
              ? <>~$20 one-time vetting + $1.50/mo campaign — <b>absorbed by iSpeedToClose</b>.</>
              : <>$4 one-time + free campaign — <b>absorbed by iSpeedToClose</b>.</>}
            <br /> Carrier review usually completes in 1–3 business days. You can keep using your number for voice calls immediately; SMS will start delivering at full reliability once approved.
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-outline text-sm"
                    data-testid="a2p-cancel">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm px-5 py-2 rounded-lg inline-flex items-center gap-2"
                    data-testid="a2p-submit">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {submitting ? "Submitting…" : "Submit for carrier review"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
